import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

interface ChartData {
  platform: string
  count: number
}

interface AnalyticsChartProps {
  data: ChartData[]
  title: string
  description?: string
}

const COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
]

export function AnalyticsChart({ data, title, description }: AnalyticsChartProps) {
  const totalCount = data.reduce((sum, item) => sum + item.count, 0)
  const pieData = data.map((item, index) => ({
    ...item,
    color: COLORS[index % COLORS.length],
  }))

  return (
    <Card className="h-full">
      <CardHeader className="text-center">
        <CardTitle className="text-lg">{title}</CardTitle>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={80}
                paddingAngle={0}
                dataKey="count"
                nameKey="platform"
              >
                {pieData.map((entry) => (
                  <Cell key={entry.platform} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const entry = payload[0]?.payload
                    if (!entry) return null
                    return (
                      <div className="bg-card border rounded-lg p-3 shadow-lg">
                        <p className="font-medium capitalize">{entry.platform}</p>
                        <p style={{ color: entry.color }}>{entry.count.toLocaleString()} users</p>
                      </div>
                    )
                  }
                  return null
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 space-y-2">
          {pieData.map((item) => (
            <div key={item.platform} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div
                  className="size-3 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-sm font-medium capitalize">{item.platform}</span>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium">{item.count.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">
                  {totalCount > 0 ? ((item.count / totalCount) * 100).toFixed(1) : "0.0"}%
                </p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
